import React, { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

let stripePromise = null

function getStripePromise() {
  if (!stripePromise) {
    stripePromise = fetch('/api/ark/stripe-config')
      .then(r => r.json())
      .then(({ publishableKey }) => publishableKey ? loadStripe(publishableKey) : null)
      .catch(() => null)
  }
  return stripePromise
}

function PaymentForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError(null)

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message)
      setProcessing(false)
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="vault-pay-form">
      <PaymentElement />
      {error && <div className="vault-pay-error">{error}</div>}
      <div className="vault-pay-form-actions">
        <button type="submit" disabled={!stripe || processing} className="vault-pay-submit">
          {processing ? 'Processing...' : 'Pay'}
        </button>
        <button type="button" onClick={onCancel} className="vault-pay-cancel">Cancel</button>
      </div>
    </form>
  )
}

export function PaywallGate({ costData, onUnlock }) {
  const [selectedAmount, setSelectedAmount] = useState(100) // cents
  const [customAmount, setCustomAmount] = useState('')
  const [clientSecret, setClientSecret] = useState(null)
  const [showCard, setShowCard] = useState(false)
  const [stripeAvailable, setStripeAvailable] = useState(null)

  useEffect(() => {
    getStripePromise().then(s => setStripeAvailable(!!s))
  }, [])

  const amounts = [
    { label: '$0', cents: 0 },
    { label: '$1', cents: 100 },
    { label: '$5', cents: 500 },
  ]

  const handleUnlockFree = async () => {
    const res = await fetch('/api/ark/payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: 0 }),
    })
    const data = await res.json()
    if (data.free) onUnlock()
  }

  const handlePay = async (cents) => {
    if (cents === 0) return handleUnlockFree()
    const res = await fetch('/api/ark/payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: cents }),
    })
    const data = await res.json()
    if (data.clientSecret) {
      setClientSecret(data.clientSecret)
      setShowCard(true)
    }
  }

  const handlePaymentSuccess = () => {
    onUnlock()
  }

  const actualCost = costData?.actual || 0.02
  const normalCost = costData?.normal || 0.089

  return (
    <div className="vault-paywall">
      <div className="vault-paywall-header">
        <h3>Unlock Full Report</h3>
        <p className="vault-paywall-sub">
          Role Summary above is free. Pay what you want to unlock the full 7-block evaluation.
        </p>
      </div>

      <div className="vault-paywall-cost">
        <div className="vault-paywall-cost-row">
          <span className="vault-paywall-cost-label">Normal API cost:</span>
          <span className="vault-paywall-cost-val">${normalCost.toFixed(3)}</span>
        </div>
        <div className="vault-paywall-cost-row">
          <span className="vault-paywall-cost-label">Your cost (Ark optimized):</span>
          <span className="vault-paywall-cost-val vault-paywall-cost-val--green">${actualCost.toFixed(3)}</span>
        </div>
      </div>

      {!showCard && (
        <>
          <div className="vault-paywall-amounts">
            {amounts.map(a => (
              <button
                key={a.cents}
                className={`vault-paywall-amt${selectedAmount === a.cents ? ' active' : ''}`}
                onClick={() => { setSelectedAmount(a.cents); setCustomAmount('') }}
              >
                {a.label}
              </button>
            ))}
            <input
              className="vault-paywall-custom"
              placeholder="Custom $"
              value={customAmount}
              onChange={e => {
                setCustomAmount(e.target.value)
                const v = parseFloat(e.target.value)
                if (!isNaN(v)) setSelectedAmount(Math.round(v * 100))
              }}
            />
          </div>

          <button
            className="vault-paywall-go"
            onClick={() => handlePay(selectedAmount)}
          >
            {selectedAmount === 0 ? 'Unlock Free' : `Pay $${(selectedAmount / 100).toFixed(2)} & Unlock`}
          </button>
        </>
      )}

      {showCard && clientSecret && stripeAvailable && (
        <Elements stripe={getStripePromise()} options={{ clientSecret, appearance: { theme: 'night' } }}>
          <PaymentForm
            clientSecret={clientSecret}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setShowCard(false)}
          />
        </Elements>
      )}

      {!stripeAvailable && stripeAvailable !== null && (
        <div className="vault-paywall-note">
          Stripe not configured. <button className="vault-paywall-link" onClick={handleUnlockFree}>Unlock free</button>
        </div>
      )}
    </div>
  )
}
